/**
 * 見積り乖離判定に関するユーティリティ
 */
import { CellClassParams } from 'ag-grid-community'
import { colorPalette } from '../../../style/colorPalette'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  isUnsetSprintRowUuid,
} from '../productBacklog'

// 小数点誤差を考慮した閾値
const VARIANCE_THRESHOLD = 0.001

// スプリントごとの見積り工数情報
export type SprintEstimatedHourInfo = {
  sprintUuid: string
  sprintName: string
  estimatedHour: number
}

// 全スプリントの見積り時間合計を取得（sprintProductItemsベース）
const getTotalSprintEstimatedHour = (pbiRow: ProductBacklogItemRow): number => {
  const spis = pbiRow.sprintProductItems || []
  return spis.reduce((total, spi) => total + (spi.estimatedHour || 0), 0)
}

// スプリント別の見積り工数リストを取得（同一スプリントは合算）
export const getSprintEstimatedHourList = (
  pbiRow: ProductBacklogItemRow
): SprintEstimatedHourInfo[] => {
  const spis = pbiRow.sprintProductItems || []
  const sprints = pbiRow.wbsItem.sprints || []

  // スプリントUUIDごとに工数を合算
  const hourBySprintUuid = new Map<string, number>()
  spis.forEach(spi => {
    const current = hourBySprintUuid.get(spi.sprintUuid) || 0
    hourBySprintUuid.set(spi.sprintUuid, current + (spi.estimatedHour || 0))
  })

  // スプリント情報を取得してリストを作成（重複排除済み）
  const result: SprintEstimatedHourInfo[] = []
  const processedSprintUuids = new Set<string>()

  sprints.forEach(sprint => {
    if (processedSprintUuids.has(sprint.uuid)) return
    processedSprintUuids.add(sprint.uuid)

    const totalHour = hourBySprintUuid.get(sprint.uuid) || 0
    result.push({
      sprintUuid: sprint.uuid,
      sprintName: sprint.name,
      estimatedHour: totalHour,
    })
  })

  return result
}

// 乖離情報を取得
export const getVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const originalHour = pbiRow.wbsItem.estimatedWorkload?.hour ?? 0
  const sprintTotalHour = getTotalSprintEstimatedHour(pbiRow)
  const hasVariance =
    Math.abs(originalHour - sprintTotalHour) > VARIANCE_THRESHOLD
  const sprintBreakdown = getSprintEstimatedHourList(pbiRow)
  return { originalHour, sprintTotalHour, hasVariance, sprintBreakdown }
}

// 乖離判定対象の行データを取得（対象外ならnull）
export const getVarianceTargetRow = (
  data: ProductBacklogRow | undefined
): ProductBacklogItemRow | null => {
  if (!data || !isProductBacklogItemRow(data)) return null
  const parentSprintUuid = data.treeValue?.[0]
  if (!parentSprintUuid || isUnsetSprintRowUuid(parentSprintUuid)) return null
  return data
}

// 乖離がある場合の背景色スタイル
export const getVarianceCellStyle = (
  params: CellClassParams<ProductBacklogRow>
): { background: string } | undefined => {
  const row = getVarianceTargetRow(params.data)
  if (!row) return undefined
  if (getVarianceInfo(row).hasVariance) {
    return { background: colorPalette.pink[1] }
  }
  return undefined
}
